/**
 * What a gesture is called, in words.
 *
 * The engine names a gesture `gesture_c_nose_wrinkled`. A reviewer reads «Nariz arrugada». Between
 * those two there is more history than there looks, and this module is where it lives.
 *
 * ## The catalogue was split, and old rows keep old names
 *
 * In September 2026 the detector's catalogue was rewritten to mirror what it can actually tell
 * apart. The `A/B/C/D` subgroup prefixes were dropped from every expression, and two families were
 * split by a distinction the old ids could not express:
 *
 * - `manipulation_face_touch` became one class per contact region (nose, chin, ear…).
 * - `emblem_mouth_twist` became `_left` and `_right`, because the detector's twist ratio is signed.
 *
 * Analyses are **not** re-written when that happens — the whole point of this product is that an
 * old analysis stays as it was. So a video analysed before the split still stores
 * `gesture_c_brows_down`, and today's video stores `expression_brows_down`, and both reach this
 * screen. Aliasing the ones with a successor is what stops the same gesture appearing twice in a
 * filter under two names.
 *
 * ## Some of them have no successor, and those are not aliased
 *
 * `gesture_a_brows_tight_down` was deleted outright: the detector stopped distinguishing it, and
 * there is no current class that means the same thing. `emblem_mouth_twist` was retired rather than
 * split, because a stored row with no side cannot be assigned to left or right without inventing
 * the answer.
 *
 * The legacy frontend drops these rows silently. **This one does not**, because a reviewer looking
 * at an old analysis and seeing thirteen of its detections vanish has no way to know why. They are
 * named, and marked as belonging to a class the engine no longer produces.
 *
 * Video 306 — a real, current analysis in this environment — is mostly made of them.
 *
 * ## Why not read `labelEs` from the catalogue endpoint
 *
 * It exists, and `gesture-settings.ts` uses it — correctly, because the configuration screen is
 * *about* the live catalogue and already has it loaded. This screen is about one analysis, and the
 * gestures in an old analysis are precisely the ones the catalogue no longer contains. Fetching it
 * here would cost a sixth request on a screen that already makes five, and still leave the
 * historical names unresolved.
 *
 * The price is drift: a gesture added after this file was written reads as its own id, made into
 * words. Ugly, correct, and never blank.
 */

/**
 * Ids stored by earlier versions, and what they are called now.
 *
 * Mirrors `LEGACY_GESTURE_NAMES` in the ai-service catalogue. Only entries with a true successor
 * belong here: an alias is a claim that two names mean the same thing.
 */
const RENAMED: Readonly<Record<string, string>> = Object.freeze({
  gesture_a_lips_pressed: 'expression_lips_pressed',
  gesture_b_corner_tight_up: 'expression_corner_tight_up',
  gesture_c_brows_down: 'expression_brows_down',
  gesture_c_nose_wrinkled: 'expression_nose_wrinkled',
  gesture_c_upper_lip_inverted_u: 'expression_upper_lip_inverted_u',
  gesture_d_brows_up_joined: 'expression_brows_up',
  gesture_d_lips_stretched_back: 'expression_lips_stretched_back',
});

/**
 * Classes the engine no longer produces, with no current equivalent.
 *
 * Kept and labelled rather than dropped. An analysis from before the split is still a valid
 * analysis, and its detections are still what the engine saw at the time.
 */
const RETIRED: Readonly<Record<string, string>> = Object.freeze({
  gesture_a_brows_tight_down: 'Cejas muy bajadas',
  expression_brows_tight_down: 'Cejas muy bajadas',
  emblem_mouth_twist: 'Boca torcida',
  manipulation_face_touch: 'Se toca la cara',
  manipulation_nose_touch_base: 'Se toca la base de la nariz',
  manipulation_nose_touch_bridge: 'Se toca el puente de la nariz',
});

/** The current catalogue, in the interface's language. */
const LABELS: Readonly<Record<string, string>> = Object.freeze({
  illustrator_hand_gestures: 'Gesticula con las manos',
  manipulation_hand_massage: 'Se masajea las manos',
  manipulation_touch_nose: 'Se toca la nariz',
  manipulation_touch_index_below_nose: 'Índice bajo la nariz',
  manipulation_touch_pinch_nose: 'Se pellizca la nariz',
  manipulation_touch_mustache: 'Se toca el bigote',
  manipulation_touch_chin: 'Se toca la barbilla',
  manipulation_touch_neck: 'Se toca el cuello',
  manipulation_touch_cranium: 'Se toca la cabeza',
  manipulation_touch_eye: 'Se toca el ojo',
  manipulation_touch_ear: 'Se toca la oreja',
  speech_volume_low: 'Volumen bajo',
  speech_volume_medium: 'Volumen medio',
  speech_volume_high: 'Volumen alto',
  speech_tone_low: 'Tono grave',
  speech_tone_medium: 'Tono medio',
  speech_tone_high: 'Tono agudo',
  speech_speed_slow: 'Habla despacio',
  speech_speed_medium: 'Velocidad media',
  speech_speed_fast: 'Habla deprisa',
  emblem_shrug: 'Se encoge de hombros',
  emblem_mouth_twist_left: 'Tuerce la boca a la izquierda',
  emblem_mouth_twist_right: 'Tuerce la boca a la derecha',
  emblem_head_yes: 'Asiente',
  emblem_head_no: 'Niega con la cabeza',
  emblem_head_tilt_left: 'Ladea la cabeza a la izquierda',
  emblem_head_tilt_right: 'Ladea la cabeza a la derecha',
  gaze_deviation: 'Desvía la mirada',
  expression_lips_pressed: 'Labios apretados',
  expression_corner_tight_up: 'Comisura tensa hacia arriba',
  expression_brows_down: 'Cejas bajadas',
  expression_nose_wrinkled: 'Nariz arrugada',
  expression_upper_lip_inverted_u: 'Labio superior en U invertida',
  expression_brows_up: 'Cejas levantadas',
  expression_lips_stretched_back: 'Labios estirados',
  expression_smiling: 'Sonríe',
  expression_eyes_wide_open: 'Ojos muy abiertos',
  expression_eyes_closed: 'Ojos cerrados',
});

/**
 * The current name of a gesture.
 *
 * A name with no successor is returned unchanged rather than mapped to something near it: guessing
 * would merge two classes the engine deliberately separated.
 */
export function currentGestureName(name: string): string {
  return RENAMED[name.trim().toLowerCase()] ?? name;
}

/** Whether this gesture belongs to a class the detector no longer produces. */
export function isRetiredGesture(name: string): boolean {
  return RETIRED[currentGestureName(name).trim().toLowerCase()] !== undefined;
}

/**
 * What to call a gesture on screen.
 *
 * An unknown id becomes its own words rather than a blank: the catalogue grows with every release
 * and this screen is not worth a deploy to show one new name. `manipulation_touch_wrist` reading as
 * «Manipulation touch wrist» is ugly and perfectly usable; an empty cell is neither.
 */
export function gestureNameLabel(name: string | null | undefined): string {
  if (!name) return 'Sin nombre';

  const current = currentGestureName(name).trim().toLowerCase();

  return LABELS[current] ?? RETIRED[current] ?? humanise(current);
}

function humanise(name: string): string {
  const spaced = name.replace(/_/g, ' ');

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Why the engine filed a detection where it did.
 *
 * These are `alert.reason`, and they are two things at once: for a detection that counted, the
 * frequency band it fell in; for one that did not, the reason it was dropped. The engine uses one
 * field for both, so the labels have to read sensibly in either role.
 */
const ALERT_REASONS: Readonly<Record<string, string>> = Object.freeze({
  rare: 'Poco frecuente en esta persona',
  intermediate: 'Frecuencia intermedia: aviso',
  habitual: 'Habitual en esta persona',
  at_baseline: 'En su línea base',
  sustained_not_valid: 'Sostenido, pero no válido',
  suppressed_subject_speech: 'Ocurrió mientras hablaba',
  too_short: 'Demasiado corto (menos de 200 ms)',
  while_speaking: 'Ocurrió mientras hablaba',
  in_baseline: 'En su línea base',
  below_threshold: 'Por debajo del umbral',
  not_sustained: 'No se mantuvo lo suficiente',
  low_confidence: 'Confianza insuficiente del modelo',
});

/**
 * Why a detection reads the way it does.
 *
 * An unrecognised reason shows as itself. A decision nobody can explain is worse than one with an
 * ugly name — the reason is the only thing that makes a discarded detection auditable.
 */
export function gestureReasonLabel(reason: string | null | undefined): string {
  if (!reason) return 'Sin motivo registrado';

  return ALERT_REASONS[reason.trim().toLowerCase()] ?? reason;
}

/** `gesture` is an instant, `sustained` is a posture that held. */
export function classificationLabel(classification: string | null | undefined): string {
  if (classification === 'gesture') return 'Puntual';
  if (classification === 'sustained') return 'Sostenido';

  return classification ?? 'Sin clasificar';
}

/**
 * How the engine decided: against this person's own baseline, or against an absolute count.
 *
 * Worth showing because it changes what the verdict means. «Rare» measured against a baseline is a
 * statement about this person; measured absolutely it is a statement about the recording.
 */
export function analysisMethodLabel(method: string | null | undefined): string {
  if (method === 'baseline') return 'Comparado con su línea base';
  if (method === 'absolute') return 'Recuento absoluto';

  return method ?? 'Método sin registrar';
}
